import { cn } from '@portals-platform/ui';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { controlClasses } from '../lib/control-classes.js';
import { useFieldA11y } from '../lib/field-context.js';
import { useFieldError } from '../lib/use-field-error.js';

export interface NumberFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  name: string;
  /** Locale for grouping separators. Defaults to the browser's. */
  locale?: string;
  /** ISO 4217 code. Supplying it formats the resting value as currency. */
  currency?: string;
  /** Render as a percentage. The stored value is the number the user typed (12.5 → "12.5%"). */
  percent?: boolean;
  decimals?: number;
  prefixIcon?: React.ReactNode;
}

/**
 * Numeric input that stores a `number | undefined`, never a string.
 *
 * A plain `<input type="number">` registered with RHF yields a string, so a `z.number()`
 * schema fails unless every caller remembers `{ valueAsNumber: true }`. This owns the
 * conversion in one place.
 *
 * It shows a formatted value at rest and the raw digits while focused — editing a string
 * that already contains grouping separators and a currency symbol is miserable.
 */
export function NumberField({
  name,
  locale,
  currency,
  percent,
  decimals,
  prefixIcon,
  className,
  onBlur,
  onFocus,
  ...inputProps
}: NumberFieldProps) {
  const { control } = useFormContext();
  const hasError = !!useFieldError(name);
  const a11y = useFieldA11y(name);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  const fractionDigits = decimals ?? (currency ? 2 : 0);

  const format = React.useCallback(
    (value: number) =>
      new Intl.NumberFormat(locale, {
        ...(currency ? { style: 'currency' as const, currency } : {}),
        ...(percent ? { style: 'percent' as const } : {}),
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: Math.max(fractionDigits, percent ? 4 : fractionDigits),
      }).format(percent ? value / 100 : value),
    [locale, currency, percent, fractionDigits],
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const value = typeof field.value === 'number' ? field.value : undefined;
        const display = editing ? draft : value === undefined ? '' : format(value);

        return (
          <div className={prefixIcon ? 'relative' : undefined}>
            {prefixIcon && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 size-[15px] -translate-y-1/2 text-muted-foreground [&>svg]:size-full"
              >
                {prefixIcon}
              </span>
            )}
            <input
              {...a11y}
              // A text input with inputMode="decimal" gets the numeric keypad on mobile
              // while still allowing a formatted resting value, which type="number" forbids.
              type="text"
              inputMode="decimal"
              value={display}
              className={controlClasses({
                hasError,
                hasPrefixIcon: !!prefixIcon,
                className: cn('tabular-nums', className),
              })}
              onFocus={(event) => {
                setDraft(value === undefined ? '' : String(value));
                setEditing(true);
                onFocus?.(event);
              }}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={(event) => {
                setEditing(false);
                const cleaned = draft.replace(/[^0-9.eE+-]/g, '');
                const parsed = Number.parseFloat(cleaned);
                field.onChange(cleaned.trim() === '' || Number.isNaN(parsed) ? undefined : parsed);
                field.onBlur();
                onBlur?.(event);
              }}
              {...inputProps}
            />
          </div>
        );
      }}
    />
  );
}

/** `NumberField` preset for money. */
export function CurrencyField({ currency = 'USD', ...props }: NumberFieldProps) {
  return <NumberField currency={currency} {...props} />;
}

/** `NumberField` preset for percentages. Stores `12.5` for "12.5%". */
export function PercentField(props: NumberFieldProps) {
  return <NumberField percent decimals={1} {...props} />;
}
